package data.db.delight.daos

import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import data.db.delight.AppDatabase
import data.db.delight.FavoriteVideo as FavoriteVideoRow
import features.videoDetails.data.model.FavoriteEntity
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import models.FavoriteVideo
import utils.IO_MULTI
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

class FavoriteVideoDao(private val database: AppDatabase) {

    private suspend fun queries() = database.queries()

    @OptIn(ExperimentalTime::class)
    suspend fun saveFavoriteVideo(video: FavoriteVideo) {
        val q = queries() ?: return
        q.transaction {
            q.insertFavoriteVideo(
                id = video.videoId.toLong(),
                imdbId = null,
                imageUrl = video.imageUrl,
                title = video.title,
                isTvShow = if (video.isTvShow) 1L else 0L,
                isDeleted = 0L,
                changedAt = Clock.System.now().toString(),
            )
        }
    }

    suspend fun deleteAll() {
        val q = queries() ?: return
        q.transaction {
            q.deleteAllFavorites()
        }
    }

    /** One transaction, so listeners do not see an empty list between delete and insert. */
    suspend fun replaceAll(videos: List<FavoriteEntity>) {
        val q = queries() ?: return
        q.transaction {
            q.deleteAllFavorites()
            videos.forEach { video ->
                q.insertFavoriteVideo(
                    id = video.videoId.toLong(),
                    imdbId = null,
                    imageUrl = video.imageUrl,
                    title = video.title,
                    isTvShow = if (video.isTvShow) 1L else 0L,
                    isDeleted = if (video.isDeleted) 1L else 0L,
                    changedAt = video.changedAt,
                )
            }
        }
    }

    suspend fun saveAllFavoriteVideo(videos: List<FavoriteEntity>) {
        val q = queries() ?: return
        q.transaction {
            videos.forEach { video ->
                q.insertFavoriteVideo(
                    id = video.videoId.toLong(),
                    imdbId = null,
                    imageUrl = video.imageUrl,
                    title = video.title,
                    isTvShow = if (video.isTvShow) 1L else 0L,
                    isDeleted = if (video.isDeleted) 1L else 0L,
                    changedAt = video.changedAt,
                )
            }
        }
    }

    /** Null when the database is not ready yet. */
    suspend fun snapshot(): List<FavoriteVideoRow>? {
        val q = queries() ?: return null
        return q.getAllFavoriteVideo().awaitAsList()
    }

    suspend fun getAllFavoriteVideos(): Flow<List<FavoriteVideoRow>>? {
        return queries()?.getAllFavoriteVideo()?.asFlow()?.mapToList(Dispatchers.IO_MULTI)
    }

    @OptIn(ExperimentalTime::class)
    suspend fun deleteFavouriteVideo(video: FavoriteVideo) {
        val q = queries() ?: return
        q.transaction {
            q.insertFavoriteVideo(
                id = video.videoId.toLong(),
                imdbId = null,
                imageUrl = video.imageUrl,
                title = video.title,
                isTvShow = if (video.isTvShow) 1L else 0L,
                isDeleted = if (video.isDeleted) 1L else 0L,
                changedAt = Clock.System.now().toString(),
            )
        }
    }

    suspend fun isVideoFavorite(videoId: Int): Long? {
        return queries()?.isVideoFavorite(id = videoId.toLong())?.awaitAsOneOrNull()
    }
}
